import { Injectable } from '@angular/core';
import { ProfileCompletionItem } from '@core/enums';
import { ItemModalConfigProfileScore } from '@core/interface';

/**
 * Service responsável por gerenciar as configurações de modais dos itens do profile score.
 *
 * Centraliza todas as configurações de modais (título, conteúdo, passos, etc.) para facilitar
 * a manutenção e permitir que essas configurações sejam facilmente atualizadas ou
 * até carregadas dinamicamente de uma API no futuro.
 *
 * @example
 * const config = this.profileModalConfigService.getModalConfig(ProfileCompletionItem.CONNECT_CAR);
 * if (config) {
 *   // Exibir modal com a configuração
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileModalConfigService {
  /**
   * Configuração de modais por item do profile.
   * Apenas os itens presentes neste mapa terão o link de "Como conectar?" exibido.
   *
   * Para adicionar um novo modal:
   * 1. Adicione uma entrada neste objeto com o ProfileCompletionItem correspondente
   * 2. Configure linkLabel, component e inputs
   * 3. O componente automaticamente exibirá o link e o modal
   */
  private readonly modalConfigs: Partial<Record<ProfileCompletionItem, ItemModalConfigProfileScore>> = {
    [ProfileCompletionItem.CONNECT_CAR]: {
      linkLabel: 'profile-score.how_to_connect',
      inputs: {
        title: 'profile-score.modal.connect_car.title',
        subtitle: 'profile-score.modal.connect_car.subtitle',
        steps: [
          'profile-score.modal.connect_car.step_1',
          'profile-score.modal.connect_car.step_2',
          'profile-score.modal.connect_car.step_3',
        ],
        buttonText: 'profile-score.modal.connect_car.button',
      },
    },
    // Adicione aqui novos itens que precisam de modal
    // Exemplo:
    // [ProfileCompletionItem.VIN]: {
    //   linkLabel: 'profile-score.where_to_find',
    //   inputs: {
    //     title: 'Onde encontrar o VIN?',
    //     subtitle: 'O VIN é o número de identificação do veículo.',
    //     steps: [
    //       'Verifique o <strong>documento do veículo</strong>.',
    //       'Olhe no <strong>canto inferior do para-brisa</strong> do lado do motorista.',
    //       'Confira a <strong>etiqueta na porta do motorista</strong>.',
    //     ],
    //     buttonText: 'Entendi',
    //   },
    // },
  };

  /**
   * Obtém a configuração de modal para um item específico.
   *
   * @param item - O item do profile completion
   * @returns A configuração do modal ou undefined se o item não tiver modal configurado
   */
  getModalConfig(item: ProfileCompletionItem): ItemModalConfigProfileScore | undefined {
    return this.modalConfigs[item];
  }

  /**
   * Obtém o label do link para um item específico.
   * Este é o texto que aparece como link clicável (ex: "Como conectar?").
   *
   * @param item - O item do profile completion
   * @returns O label do link ou undefined se o item não tiver modal configurado
   */
  getLinkLabel(item: ProfileCompletionItem): string | undefined {
    return this.modalConfigs[item]?.linkLabel;
  }
}
